import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchSettings, generateAgentDraft, saveCustomAgent } from '../services/api';
import { Button, Card, Field, Loading, ErrorText, errorMessage } from '../components/ui';
import ReferenceDocumentPicker from '../components/ReferenceDocumentPicker';

// Only a fallback for when Settings can't be read - the real list is the
// tenant's own configured upload types, so a type added in Settings shows up
// here without a code change.
const FALLBACK_CATEGORIES = ['Drawings', 'Specifications', 'Instructions', 'Contract', 'Images', 'Reports'];

// Union of the tenant's configured upload types and anything the agent is
// already scoped to - so a type later renamed or removed in Settings doesn't
// silently drop off an agent the next time someone opens and saves it.
function categoryOptions(uploadTypes, appliesTo) {
  const configured = uploadTypes?.length ? uploadTypes : FALLBACK_CATEGORIES;
  const out = [...configured];
  for (const c of appliesTo || []) {
    if (!out.some((x) => String(x).toLowerCase() === String(c).toLowerCase())) out.push(c);
  }
  return out;
}

// Mirrors the mobile create flow: describe the agent in plain English, let the
// backend draft it (name, instructions and - critically - which upload
// categories it runs on), then review before saving.
export default function AgentEditPage() {
  const navigate = useNavigate();
  const existing = useLocation().state?.agent || null;

  const [description, setDescription] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [agent, setAgent] = useState(existing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadTypes, setUploadTypes] = useState([]);

  // Read the tenant's upload types once so custom ones created in Settings are
  // offered here. Non-fatal on failure - the fallback list still works.
  useEffect(() => {
    let mounted = true;
    fetchSettings()
      .then((settings) => { if (mounted) setUploadTypes(settings?.uploadTypes || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  async function handleDraft() {
    setDrafting(true);
    setError('');
    try {
      const draft = await generateAgentDraft(description);
      setAgent({ ...(draft || {}), isCustom: true });
    } catch (err) { setError(errorMessage(err)); } finally { setDrafting(false); }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      // autoRun must be sent explicitly: the store treats a missing field as
      // true, so omitting it would silently re-enable auto-run every time a
      // manual-only agent was edited.
      await saveCustomAgent({ ...agent, autoRun: agent.autoRun !== false });
      navigate('/agents');
    } catch (err) { setError(errorMessage(err)); } finally { setSaving(false); }
  }

  const update = (key, value) => setAgent((prev) => ({ ...(prev || {}), [key]: value }));
  const categories = agent?.appliesTo || agent?.categories || [];

  return (
    <>
      <h1>{existing ? 'Edit agent' : 'New agent'}</h1>
      <ErrorText>{error}</ErrorText>

      {!existing && (
        <Card title="Describe it" hint="The backend drafts the agent from your description, including which upload types it should run on.">
          <textarea className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Check site photos for missing edge protection and flag anything unsafe" />
          <Button onClick={handleDraft} disabled={!description.trim() || drafting} style={{ marginTop: 10 }}>
            {drafting ? 'Drafting…' : 'Generate draft'}
          </Button>
        </Card>
      )}

      {drafting && <Loading text="Asking the AI to draft this agent…" />}

      {agent && (
        <Card title="Review">
          <Field label="Name"><input className="input" value={agent.label || ''} onChange={(e) => update('label', e.target.value)} /></Field>
          <Field label="Description"><input className="input" value={agent.description || ''} onChange={(e) => update('description', e.target.value)} /></Field>
          <Field label="Instructions"><textarea className="input" style={{ minHeight: 160 }} value={agent.instructions || agent.prompt || ''} onChange={(e) => update('instructions', e.target.value)} /></Field>
          <Field label="Runs on" hint="An agent with no categories never runs automatically, and won't be offered on a document.">
            <div className="pill-wrap">
              {categoryOptions(uploadTypes, categories).map((c) => {
                const active = categories.some((x) => String(x).toLowerCase() === c.toLowerCase());
                return (
                  <button
                    key={c}
                    type="button"
                    className={`pill${active ? ' active' : ''}`}
                    onClick={() => update('appliesTo', active ? categories.filter((x) => String(x).toLowerCase() !== c.toLowerCase()) : [...categories, c])}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </Field>
          <ReferenceDocumentPicker
            value={agent.referenceDocuments || []}
            onChange={(docs) => update('referenceDocuments', docs)}
          />
          <Field label="Run automatically on upload" hint="When off, the agent stays available to run by hand from a document, but never fires on its own.">
            <label className="check-row">
              <input
                type="checkbox"
                checked={agent.autoRun !== false}
                onChange={(e) => update('autoRun', e.target.checked)}
              />
              <span>{agent.autoRun !== false ? 'Runs by itself whenever a matching document is uploaded.' : 'Only runs when someone chooses it from a document.'}</span>
            </label>
          </Field>
          <Button onClick={handleSave} disabled={saving || !agent.label}>{saving ? 'Saving…' : 'Save agent'}</Button>
        </Card>
      )}
    </>
  );
}
